import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Banknote, CheckCircle2, CreditCard, ExternalLink, Loader2, AlertCircle, X } from 'lucide-react';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { apiClient } from '../../lib/apiClient';

// CTV-518: CTVBuyer is merchant of record — advertiser charges land on the
// platform account and each publisher's share is released as a Stripe
// transfer 60–90 days after the charge. This page only connects a payout
// destination (recipient account via hosted onboarding, or the publisher's
// own Stripe account via Connect OAuth) and shows readiness. The
// pending/released payout ledger view is a follow-up ticket.

interface PublisherStripeRow {
  slug: string;
  stripe_account_id: string | null;
  stripe_account_mode: 'recipient' | 'oauth' | null;
  stripe_payouts_enabled: boolean;
  stripe_details_submitted: boolean;
  stripe_onboarding_started_at: string | null;
  stripe_onboarding_completed_at: string | null;
}

// Shape returned by GET /stations/:slug — only the bits we read here.
interface StationApi {
  slug: string;
  stripe: {
    accountId: string | null;
    accountMode: 'recipient' | 'oauth' | null;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    onboardingStartedAt: string | null;
    onboardingCompletedAt: string | null;
  };
}

const PAYOUT_TIMING_NOTE =
  'Your share of each charge is released 60–90 days after payment. A payout history view is coming soon.';

const PublisherPaymentsPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const [params, setParams] = useSearchParams();
  const [row, setRow] = React.useState<PublisherStripeRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [actionBusy, setActionBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Dismissible notice for the ?stripe_oauth=connected|denied return trip.
  const [oauthNotice, setOauthNotice] = React.useState<'connected' | 'denied' | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      const station = await apiClient.get<StationApi>(`/stations/${encodeURIComponent(slug)}`);
      setRow({
        slug: station.slug,
        stripe_account_id: station.stripe.accountId,
        stripe_account_mode: station.stripe.accountMode,
        stripe_payouts_enabled: station.stripe.payoutsEnabled,
        stripe_details_submitted: station.stripe.detailsSubmitted,
        stripe_onboarding_started_at: station.stripe.onboardingStartedAt,
        stripe_onboarding_completed_at: station.stripe.onboardingCompletedAt,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load station');
    }
    setLoading(false);
  }, [slug]);

  // After return from Stripe, ask the API to re-fetch and persist account state.
  const refreshFromStripe = React.useCallback(async () => {
    try {
      await apiClient.post('/stripe/account-status', { slug });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to refresh status');
    }
  }, [slug, refresh]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // If we just came back from Stripe-hosted onboarding, refresh status.
  React.useEffect(() => {
    if (params.get('return') === '1' && row?.stripe_account_id) {
      refreshFromStripe();
    }
  }, [params, row?.stripe_account_id, refreshFromStripe]);

  // OAuth return trip: surface the outcome and (on success) refresh status.
  React.useEffect(() => {
    const outcome = params.get('stripe_oauth');
    if (outcome !== 'connected' && outcome !== 'denied') return;
    setOauthNotice(outcome);
    if (outcome === 'connected') refreshFromStripe();
    // Strip the param so a reload doesn't re-trigger the notice.
    const next = new URLSearchParams(params);
    next.delete('stripe_oauth');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startBankOnboarding = async () => {
    setActionBusy(true);
    setError(null);
    try {
      const baseReturn = `${window.location.origin}/admin/${slug}/payments`;
      const { url } = await apiClient.post<{ url: string }>('/stripe/connect-link', {
        slug,
        returnUrl: `${baseReturn}?return=1`,
        refreshUrl: `${baseReturn}?refresh=1`,
      });
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start onboarding');
      setActionBusy(false);
    }
  };

  const startOauthConnect = async () => {
    setActionBusy(true);
    setError(null);
    try {
      const { url } = await apiClient.post<{ url: string }>('/stripe/oauth-link', {
        slug,
        returnUrl: `${window.location.origin}/admin/${slug}/payments`,
      });
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start Stripe connection');
      setActionBusy(false);
    }
  };

  const isOauth = row?.stripe_account_mode === 'oauth';
  const isConnected = !!row?.stripe_account_id && (isOauth || row?.stripe_details_submitted);

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">Payouts</h1>
      <p className="text-sm text-slate-600 mb-8">
        CTVBuyer collects payment from advertisers and sends your share of each campaign.
        Connect a payout method to receive transfers — your share of each charge is released
        60–90 days after the advertiser is charged.
      </p>

      {oauthNotice && (
        <div
          className={`mb-6 flex items-start justify-between gap-3 rounded-lg border p-4 text-sm ${
            oauthNotice === 'connected'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}
        >
          <span>
            {oauthNotice === 'connected'
              ? 'Stripe account connected. Payouts will be sent to this account.'
              : 'Stripe connection was canceled — no account was connected.'}
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setOauthNotice(null)}
            className="shrink-0 text-current hover:opacity-70"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : isConnected && row ? (
        <ConnectedCard row={row} onRefresh={refreshFromStripe} />
      ) : row?.stripe_account_id ? (
        <InProgressCard row={row} onResume={startBankOnboarding} onRefresh={refreshFromStripe} busy={actionBusy} />
      ) : (
        <NotConnectedCard onConnectBank={startBankOnboarding} onConnectStripe={startOauthConnect} busy={actionBusy} />
      )}

      {error && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}
    </div>
  );
};

function NotConnectedCard({
  onConnectBank,
  onConnectStripe,
  busy,
}: {
  onConnectBank: () => void;
  onConnectStripe: () => void;
  busy: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3"><Banknote className="w-5 h-5 text-slate-700" /><h2 className="text-base font-semibold text-slate-900">No payout method connected</h2></div>
      <p className="text-sm text-slate-600 mb-5">
        Connect a bank account through Stripe&apos;s secure setup page — no Stripe account or login
        needed. Already use Stripe for your business? Connect your existing account instead.
      </p>
      <div className="flex flex-wrap gap-2">
        <button onClick={onConnectBank} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#635bff] hover:bg-[#4d44e0] disabled:opacity-60 text-white text-sm font-medium rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
          Connect bank account
        </button>
        <button onClick={onConnectStripe} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-medium rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Connect existing Stripe account
        </button>
      </div>
      <p className="mt-4 text-xs text-slate-500">{PAYOUT_TIMING_NOTE}</p>
    </div>
  );
}

function InProgressCard({ row, onResume, onRefresh, busy }: { row: PublisherStripeRow; onResume: () => void; onRefresh: () => void; busy: boolean }) {
  return (
    <div className="bg-white border border-amber-200 bg-amber-50 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3"><AlertCircle className="w-5 h-5 text-amber-700" /><h2 className="text-base font-semibold text-slate-900">Payout setup in progress</h2></div>
      <p className="text-sm text-slate-700 mb-2">Stripe account: <span className="font-mono text-xs">{row.stripe_account_id}</span></p>
      <p className="text-sm text-slate-700 mb-5">Details submitted: {row.stripe_details_submitted ? 'yes' : 'no'} · Payouts enabled: {row.stripe_payouts_enabled ? 'yes' : 'no'}</p>
      <div className="flex gap-2">
        <button onClick={onResume} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-[#635bff] hover:bg-[#4d44e0] disabled:opacity-60 text-white text-sm font-medium rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Resume setup
        </button>
        <button onClick={onRefresh} className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium rounded-md">Refresh status</button>
      </div>
    </div>
  );
}

function ConnectedCard({ row, onRefresh }: { row: PublisherStripeRow; onRefresh: () => void }) {
  const isOauth = row.stripe_account_mode === 'oauth';
  const methodLabel = isOauth ? 'Stripe account' : 'Bank account (via Stripe)';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <h2 className="text-base font-semibold text-slate-900">Payout method connected</h2>
      </div>
      <div className="space-y-2 text-sm text-slate-700 mb-5">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-slate-500" />
          <span>Payout method: <span className="font-medium">{methodLabel}</span></span>
        </div>
        <div>
          Payouts enabled:{' '}
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded ${
              row.stripe_payouts_enabled
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {row.stripe_payouts_enabled ? 'yes' : 'no'}
          </span>
        </div>
      </div>
      <button onClick={onRefresh} className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium rounded-md">
        Refresh status
      </button>
      <p className="mt-4 text-xs text-slate-500">{PAYOUT_TIMING_NOTE}</p>
      {isOauth && (
        <p className="mt-1 text-xs text-slate-500">Manage payouts in your Stripe dashboard.</p>
      )}
    </div>
  );
}

export default PublisherPaymentsPage;
